import {
  readLocalAppearance,
  writeLocalAppearance,
} from "@/utils/appearanceStorage";

// Оформление = user.theme ({name, color, dark_mode}) + фон (background_image).
//
// Применённое оформление живёт там же, где и раньше: state.key.user.theme и
// state.key.background_image — их читают MainLayout и все экраны.
// Здесь дополнительно хранится копия из базы (db), чтобы при выключенной
// синхронизации эхо с других устройств меняло только её, а не экран, и чтобы
// при включении синхронизации на другом устройстве было что применить.

const DEFAULT_THEME = { name: "default", color: "#0077FF", dark_mode: false };
const DEFAULT_BACKGROUND = "Light Blue";

export function normalizeTheme(theme) {
  const t = theme || {};
  return {
    name: t.name || DEFAULT_THEME.name,
    color: t.color || DEFAULT_THEME.color,
    dark_mode: !!t.dark_mode,
  };
}

const sameTheme = (a, b) =>
  !!a &&
  !!b &&
  a.name == b.name &&
  a.color == b.color &&
  !!a.dark_mode == !!b.dark_mode;

// то, что сейчас на экране
function currentAppearance(rootState) {
  const user = rootState.key.user || {};
  return {
    theme: normalizeTheme(user.theme),
    background_image:
      rootState.key.background_image ||
      user.background_image ||
      DEFAULT_BACKGROUND,
  };
}

export default {
  state: () => ({
    db: { theme: null, background_image: null },
  }),
  getters: {
    // поля нет у старых пользователей (lean без default) — это «синхронизация включена»
    isThemeSync: (s, g, rootState) =>
      !rootState.key.user || rootState.key.user.theme_sync !== false,
  },
  mutations: {
    setDbAppearance(s, patch) {
      s.db = { ...s.db, ...patch };
    },
  },
  actions: {
    // После загрузки пользователя (GET_DATA / fetchUserData): в user пришло
    // оформление из базы. Без синхронизации поверх него кладём локальное.
    appearanceInit({ commit, dispatch, getters, rootState }) {
      const user = rootState.key.user;
      if (!user || !user._id) return;

      commit("setDbAppearance", {
        theme: normalizeTheme(user.theme),
        background_image: user.background_image || DEFAULT_BACKGROUND,
      });

      if (getters.isThemeSync) return;

      const local = readLocalAppearance(user._id);
      if (!local) {
        // первый вход на этом устройстве с выключенной синхронизацией —
        // стартуем с текущего оформления и дальше живём локально
        writeLocalAppearance(user._id, currentAppearance(rootState));
        return;
      }

      return dispatch("appearanceApply", {
        theme: { ...normalizeTheme(user.theme), ...(local.theme || {}) },
        background_image: local.background_image,
      });
    },

    // Показать оформление (без сохранения куда-либо)
    appearanceApply({ commit, rootState }, { theme, background_image } = {}) {
      const user = rootState.key.user;
      if (!user) return;

      if (theme) {
        const current = normalizeTheme(user.theme);
        if (theme.color && theme.color != current.color)
          commit("changeUserColor", theme.color);
        if (theme.name && theme.name != current.name)
          commit("changeUserTheme", theme.name);
        if (
          typeof theme.dark_mode == "boolean" &&
          theme.dark_mode != current.dark_mode
        )
          commit("changeUserDarkMode", theme.dark_mode);
      }

      if (
        background_image &&
        (background_image != rootState.key.background_image ||
          background_image != user.background_image)
      ) {
        commit("userBackgoundImageChange", background_image);
      }
    },

    // Изменение темы из настроек (цвет / стиль / светлая-тёмная)
    async saveTheme({ dispatch, getters, rootState }, patch) {
      const theme = { ...normalizeTheme(rootState.key.user.theme), ...patch };
      await dispatch("appearanceApply", { theme });

      if (!getters.isThemeSync) {
        writeLocalAppearance(
          rootState.key.user._id,
          currentAppearance(rootState)
        );
        return;
      }

      this._vm.$socket.emit("IN_UserThemeChange", theme);
    },

    // Смена фона из настроек. true — фон сменился.
    async saveBackgroundImage({ dispatch, getters, rootState }, image_name) {
      if (!image_name) return false;

      if (!getters.isThemeSync) {
        await dispatch("appearanceApply", { background_image: image_name });
        writeLocalAppearance(
          rootState.key.user._id,
          currentAppearance(rootState)
        );
        return true;
      }

      const changed = await dispatch("saveBackgroundImageToDb", image_name);
      if (changed) {
        await dispatch("appearanceApply", { background_image: image_name });
      }
      return changed;
    },

    async saveBackgroundImageToDb({ commit, dispatch }, image_name) {
      const req = await dispatch("fetch_request", {
        token: true,
        method: "POST",
        url: this._vm.$w_api.CHANGE_BACKGROUND_IMAGE,
        body: { image_name },
      });
      if (!req || !req.ok) return false;

      const reqJSON = await req.json();
      if (reqJSON.message != "changed image") return false;

      commit("setDbAppearance", { background_image: image_name });
      return true;
    },

    // Эхо OUT_UserThemeChange / OUT_ChangeBackgroundImage (все устройства
    // пользователя, включая это). Без синхронизации экран не трогаем.
    appearanceRemoteTheme({ commit, dispatch, getters }, data) {
      const theme = normalizeTheme(data);
      commit("setDbAppearance", { theme });
      if (getters.isThemeSync) return dispatch("appearanceApply", { theme });
    },
    appearanceRemoteBackground({ commit, dispatch, getters }, image_name) {
      commit("setDbAppearance", { background_image: image_name });
      if (getters.isThemeSync)
        return dispatch("appearanceApply", { background_image: image_name });
    },

    // Переключатель в настройках на этом устройстве
    async setThemeSync({ commit, dispatch, getters, rootState }, enabled) {
      if (getters.isThemeSync == enabled) return;

      const user = rootState.key.user;
      if (!enabled) {
        // оформление этого устройства не меняется — просто дальше живёт локально
        writeLocalAppearance(user._id, currentAppearance(rootState));
      }

      commit("setChangeUserData", { type: "theme_sync", field: enabled });
      this._vm.$socket.emit("IN_UserGeneralSettings", {
        type: "theme_sync",
        field: enabled,
      });

      // включили здесь — оформление этого устройства становится общим
      if (enabled) await dispatch("pushAppearanceToDb");
    },

    async pushAppearanceToDb({ commit, dispatch, state, rootState }) {
      const current = currentAppearance(rootState);

      if (!sameTheme(current.theme, state.db.theme)) {
        this._vm.$socket.emit("IN_UserThemeChange", current.theme);
        commit("setDbAppearance", { theme: current.theme });
      }
      if (current.background_image != state.db.background_image) {
        await dispatch("saveBackgroundImageToDb", current.background_image);
      }
    },

    // OUT_UserGeneralSettings {type: "theme_sync"} — вызывается ДО того, как
    // флаг запишется в user. Переключение с этого же устройства уже применено.
    appearanceSyncChanged({ dispatch, getters, rootState, state }, enabled) {
      if (getters.isThemeSync == enabled) return;

      if (!enabled) {
        writeLocalAppearance(
          rootState.key.user._id,
          currentAppearance(rootState)
        );
        return;
      }

      // включили на другом устройстве — показываем общее оформление
      return dispatch("appearanceApply", {
        theme: state.db.theme,
        background_image: state.db.background_image,
      });
    },
  },
};
